import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserDetails,
  setFlagsInitialized,
} from "./Pages/Login/loginSlice";
import SiteLoader from "./components/site-loader/SiteLoader";
import ErrorWithLogo from "./components/site-loader/ErrorWithLogo";
import { useAuth } from "@workos-inc/authkit-react";
import { setAuthToken } from "./services/service";
import { setAxiosTokenGetter } from "./api/axios-instance";
import { initializeFeatureFlags } from "./config/ff";

export default function Authenticated({ children }) {
  const dispatch = useDispatch();
  const loginState = useSelector((state) => state.login);

  // Local user from our backend
  const {
    id: localUserId,
    isLoading: isLocalUserLoading,
    errorMessage,
    errorStatus,
    flagsInitialized,
  } = loginState;

  // WorkOS hook
  const {
    user: workOsUser,
    isLoading: isAuthKitLoading,
    getAccessToken,
    signIn,
    signOut,
  } = useAuth();

  // Listen for global logout events (e.g., from service layer on 401)
  useEffect(() => {
    const handleGlobalLogout = (event) => {
      console.log(
        "🚪 [Authenticated] Received global logout event:",
        event.detail,
      );
      // Perform WorkOS logout and return to origin
      signOut({
        returnTo: window.location.origin,
      });
    };

    // Add event listener for global logout
    window.addEventListener("workos-logout-required", handleGlobalLogout);

    // Cleanup event listener
    return () => {
      window.removeEventListener("workos-logout-required", handleGlobalLogout);
    };
  }, [signOut]);

  // 2) A useEffect that logs the user in if needed
  useEffect(() => {
    // If AuthKit is done checking but found no WorkOS session:
    // 🚨 CRITICAL: Save current path BEFORE redirecting to WorkOS
    if (!isAuthKitLoading && !workOsUser) {
      const currentPath = window.location.pathname + window.location.search;

      // Skip callback route to avoid loops, and only save non-root paths
      if (!currentPath.includes("/callback") && currentPath !== "/") {
        console.log(
          "💾 [Authenticated] Saving deeplink before WorkOS redirect:",
          currentPath,
        );
        localStorage.setItem("authRedirectPath", currentPath);
      }

      signIn();
    }
  }, [isAuthKitLoading, workOsUser, signIn]);

  // Initialize axios token getter when WorkOS user is available
  useEffect(() => {
    if (workOsUser && getAccessToken) {
      // Set the token getter for axios instance (used by generated API clients)
      // This ensures axios always gets fresh tokens from WorkOS SDK
      setAxiosTokenGetter(getAccessToken);
    }
  }, [workOsUser, getAccessToken]);

  // 3) A useEffect to fetch local user details when we HAVE a WorkOS user
  //    BUT haven't loaded local user details yet
  useEffect(() => {
    if (workOsUser && !localUserId) {
      getAccessToken().then((token) => {
        // todo - this way of setting the token is not ideal, but it is impossible to do it in the service, because the useAuth hook can be called only in a component. currently what i have in mind is to listen to route changes and/or interval to check if the token is expired and then refresh it.
        setAuthToken(token);
        // Pass the token to the backend to verify + fetch user from our DB
        dispatch(fetchUserDetails());
      });
    }
  }, [workOsUser, localUserId, dispatch, getAccessToken]);

  // Initialize Feature Flags *after* user data is available
  useEffect(() => {
    // Only run if we have user data AND flags aren't initialized yet
    if (loginState?.id && !flagsInitialized) {
      // Prepare context (ensure only necessary flat fields are passed)
      const featureFlagsContext = {
        user_id: loginState.id,
        email: loginState.email,
        name: `${loginState.firstName || ""} ${loginState.lastName || ""}`.trim(),
        customData: {
          role: loginState.role,
          entity_ids: loginState.entityIds,
          timezone: loginState.timezone,
        },
      };

      initializeFeatureFlags(featureFlagsContext).then((success) => {
        dispatch(setFlagsInitialized(success));
      });
    }
  }, [loginState, flagsInitialized, dispatch]); // Depends on loginState and initialization status

  // Authkit is loading => show loader
  if (isAuthKitLoading) {
    return <SiteLoader />;
  }

  // if no WorkOS user yet => show loader (the `signIn()` above is redirecting)
  if (!workOsUser) {
    return <SiteLoader />;
  }

  // If there's an error with local user fetch => show error
  if (errorMessage) {
    return (
      <ErrorWithLogo errorMessage={errorMessage} errorStatus={errorStatus} />
    );
  }

  // If local user is loading OR feature flags haven't initialized => show loader
  if (isLocalUserLoading || !flagsInitialized) {
    return <SiteLoader />;
  }

  // otherwise, everything is loaded successfully. Render the app.
  return <>{children}</>;
}
